/**
 * Read access to a FloorPlanRoom's optional fields: the one place each
 * optional field's default is defined. Read through these instead of
 * indexing the room directly.
 *
 * The room-level corners, primary edges, and freeElements are optional on the
 * wire: a bare rectangle room is just `{ width, length }`. A missing corner
 * IS a square corner, a missing edge has no segments, and missing
 * freeElements is empty. Those defaults are frozen singletons (stable
 * identity; accidental mutation throws). Never write through a value
 * obtained here. Write paths materialize the field first, through the edit
 * catalog's structural sharing.
 *
 * A defaulted scalar field has one reader here, and the save optimizer
 * (`getOptimizedFloorPlan`) drops a stored value only when it is the value
 * the field reads as left out (the `..._FIELD_DEFAULTS` tables). A new
 * defaulted field is its schema entry, its reader and table entry here, and
 * the one place that reads it.
 */
import {
  type CornerName,
  type EdgeName,
  type FloorPlanCorner,
  type FloorPlanEdgeSegment,
  type FloorPlanEdgeSegmentSimple,
  type FloorPlanFreeElement,
  type FloorPlanPrimaryEdge,
  type FloorPlanRoom,
} from './FloorPlanRoomSchema';

/** The default corner: no chamfer. */
export const SQUARE_CORNER: FloorPlanCorner = Object.freeze({ dx: 0, dy: 0 });

/** The default primary edge: no segments. */
export const EMPTY_EDGE: FloorPlanPrimaryEdge = Object.freeze({
  segments: Object.freeze([]) as unknown as FloorPlanEdgeSegment[],
});

const EMPTY_FREE_ELEMENTS = Object.freeze([]) as unknown as FloorPlanFreeElement[];

/** A room corner; missing = square. */
export function roomCorner(shape: FloorPlanRoom, corner: CornerName): FloorPlanCorner {
  return shape[corner] ?? SQUARE_CORNER;
}

/** A primary edge; missing = no segments. */
export function roomEdge(shape: FloorPlanRoom, edge: EdgeName): FloorPlanPrimaryEdge {
  return shape[edge] ?? EMPTY_EDGE;
}

/** A primary edge's segment list; missing edge = empty. */
export function edgeSegments(shape: FloorPlanRoom, edge: EdgeName): FloorPlanEdgeSegment[] {
  return shape[edge]?.segments ?? EMPTY_EDGE.segments;
}

/** The room's free elements; missing = empty. */
export function roomFreeElements(shape: FloorPlanRoom): FloorPlanFreeElement[] {
  return shape.freeElements ?? EMPTY_FREE_ELEMENTS;
}

/** The room fields the save optimizer drops at these values: each reads the same left out. */
export const ROOM_FIELD_DEFAULTS = { floorType: '', rotation: 0, z: 0 } as const;

/** The free element fields the save optimizer drops at these values, on every type. A type's own are in its `freeElementTypes` entry. */
export const FREE_ELEMENT_FIELD_DEFAULTS = { name: '', rotation: 0, base: 0 } as const;

/** The edge segment fields the save optimizer drops at these values. */
export const SEGMENT_FIELD_DEFAULTS = { name: '', openingBase: 0 } as const;

/** The floor type of a room that names none. */
export const DEFAULT_FLOOR_TYPE = 'Default';

/** The room's ceiling height; missing = 0, no ceiling. */
export const roomCeilingHeight = (room: FloorPlanRoom): number => room.height ?? 0;

/** The room's own floor type; missing or empty = `DEFAULT_FLOOR_TYPE`. */
export const roomFloorType = (room: FloorPlanRoom): string => room.floorType || DEFAULT_FLOOR_TYPE;

/** How far an alcove reaches out from its edge; missing = 0. */
export const alcoveDepth = (segment: FloorPlanEdgeSegment): number => segment.depth ?? 0;

/** How far above the floor an opening starts; missing = on the floor. */
export const segmentOpeningBase = (segment: FloorPlanEdgeSegmentSimple): number => segment.openingBase ?? SEGMENT_FIELD_DEFAULTS.openingBase;

/** How far above the floor a free element's bottom sits; missing = on the floor. */
export const freeElementBase = (element: FloorPlanFreeElement): number => element.base ?? FREE_ELEMENT_FIELD_DEFAULTS.base;

/** A free element's plan rotation in degrees; missing = 0, axis-aligned. */
export const freeElementRotation = (element: FloorPlanFreeElement): number => element.rotation ?? FREE_ELEMENT_FIELD_DEFAULTS.rotation;

/**
 * A room with every optional structural field materialized. The raw-scan
 * intake builds rooms in this form and mutates them in place while
 * assembling; everything downstream accepts the plain (optional) shape.
 */
export type CompleteFloorPlanRoom =
  & FloorPlanRoom
  & Record<CornerName, FloorPlanCorner>
  & Record<EdgeName, FloorPlanPrimaryEdge>
  & {
    freeElements: FloorPlanFreeElement[];
  };
